import { useTranslation } from "react-i18next";
import { Checkbox, Divider, Form, Icon, Input } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { AppointmentSettings } from "modules/DealerLocationDetails/components/Workorder/components";
import { isAdmin } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

const TIMESLOT_BASE_DATE = "2017-01-31";
const TIMESLOT_STEP_IN_SECONDS = 300;

const getTimeslotTime = (deadline: string) => (deadline ? formatDate(deadline, DATE_FORMATS_FNS.hoursMinutes) : "");
export const buildTimeslotDeadline = (time: string) => formatDate(new Date(`${TIMESLOT_BASE_DATE}T${time}:00`), DATE_FORMATS_FNS.ISOFormat);

type WorkorderProps = {
  tabId: string;
};

export const Workorder = ({ tabId }: WorkorderProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { watch, setValue } = FormHelper.useFormContext();
  const [includeVat, isNextKmAndNextDateOptional, isVideoEnabled, timeslots] = watch(["include_vat", "is_next_km_and_next_date_optional", "video_enabled", "timeslots"]);

  const isAdminUser = Boolean(user && isAdmin(user.role_id));
  const defaultCustomerOptions = [
    { key: "driver", value: "driver", text: t("v8_driver_visible", "Driver Visible") },
    { key: "contractor", value: "contractor", text: t("v8_contractor_visible", "Contractor Visible") },
    { key: "owner", value: "owner", text: t("v8_owner_visible", "Owner Visible") }
  ];

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="eye" />
        </div>
        <span className="section-title-text">{t("v8_workorder", "Workorder")}</span>
      </div>

      <AppointmentSettings />

      <Divider />
      <div className="dealer-location-columns">
        <div className="dealer-location-column">
          {isAdminUser && (
            <>
              <div className="dealer-location-subheading">{t("v8_admin_algemeen", "Admin algemeen")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox name="video_enabled" label={t("v8_video", "Video")} />
              </Form.Field>
              {isVideoEnabled && (
                <Form.Field>
                  <label>{t("v8_video_max_duration", "Video max duration in seconds")}</label>
                  <FormHelper.Field.Input name="video_capture_length_in_seconds" inputProps={{ type: "number", min: 0 }} />
                </Form.Field>
              )}
              <Form.Field>
                <FormHelper.Field.Checkbox name="schedule_enabled" label={t("v8_snooze", "Snooze")} />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="is_next_km_and_next_date_optional"
                  label={t("v8_is_next_km_and_next_date_optional", "Next km and next date optional")}
                  onChange={isOptional => !isOptional && setValue("hide_next_km_and_next_date", false)}
                />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="hide_next_km_and_next_date"
                  disabled={!isNextKmAndNextDateOptional}
                  label={t("v8_hide_next_km_and_next_date", "Hide next km and next date")}
                />
              </Form.Field>
            </>
          )}
          <div className="dealer-location-subheading">{t("v8_general_settings", "General settings")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="accept_network_metrics" label={t("v8_accept_network_metrics", "Accept network metrics")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="date_visible" label={t("v8_date", "Date")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="apk_visible" label={t("v8_apk_date", "APK Date")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="hu_visible" label={t("v8_hu_date", "HU Date")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_customer_answered_checkin_mandatory" label={t("v8_customer_sign_mandatory", "Customer sign mandatory")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_three_departments_enabled" label={t("v8_three_departments_enabled", "Three departments")} />
          </Form.Field>
        </div>

        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_visible_on_detail_page", "Visible on detail page")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="car_ready_btn_visible" label={t("v8_car_ready_button", "Car ready button")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="dp_driver_visible" label={t("v8_driver", "Driver")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="dp_contractor_visible" label={t("v8_contractor", "Contractor")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="dp_owner_visible" label={t("v8_owner", "Owner")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="service_box_visible_on_car" label={t("v8_service_button_car_dashboard", "Service button car dashboard")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="service_box_visible_on_wo" label={t("v8_service_button_wo_dashboard", "Service button WO dashboard")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="dms_price_enabled" label={t("v8_dms_price_enabled", "DMS intervention price")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="dms_billing_enabled" label={t("v8_activate_dms_billing", "Activate DMS billing")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="include_vat" label={t("v8_including_price_vat", "Including price with VAT %")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="appointment_detail_users_indicator_enabled" label={t("v8_active_users_indicator", "Active users indicator")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_car_in_shop_print_enabled" label={t("v8_car_in_shop_print", "Car in shop print")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_editing_question_videos_enabled" label={t("v8_edit_question_videos", "Edit question videos")} />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_enter_your_vat", "Enter your VAT %")}</label>
            <FormHelper.Field.Input name="vat" inputProps={{ type: "number", min: 0, disabled: !includeVat }} />
          </Form.Field>
        </div>

        <div className="dealer-location-column">
          <div className="dealer-location-subheading">{t("v8_visible_on_main_screen", "Visible on main screen")}</div>
          <Form.Field>
            <label>{t("v8_default_customer", "Default customer")}</label>
            <FormHelper.Field.Dropdown name="default_customer" options={defaultCustomerOptions} />
          </Form.Field>

          <div className="dealer-location-subheading">{t("v8_parts_load_order_times", "Parts / Load order times")}</div>
          {timeslots?.map((timeslot, idx) => (
            <Form.Field key={idx} className="dealer-location-timeslot">
              <Checkbox checked={timeslot.active} onChange={(_, { checked }) => setValue("timeslots", timeslots.with(idx, { ...timeslot, active: checked === true }))} />
              <Input
                type="time"
                step={TIMESLOT_STEP_IN_SECONDS}
                value={getTimeslotTime(timeslot.deadline)}
                onChange={(_, { value }) => setValue("timeslots", timeslots.with(idx, { ...timeslot, deadline: buildTimeslotDeadline(value) }))}
              />
            </Form.Field>
          ))}
        </div>
      </div>
    </Element>
  );
};
